import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Dialog,
  Grid,
  InputAdornment,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography,
  Divider,
  Button,
  Avatar,
} from "@mui/material";
import MutualFundIcon from "../../../assets/icons/mutualFund.svg";
import StockIcon from "../../../assets/icons/stock.svg";
import LifeInsuranceIcon from "../../../assets/icons/life-insurance.svg";
import HealthInsuranceIcon from "../../../assets/icons/healthInsurance.svg";
import arrowRight from "../../../assets/icons/adaptive-search-arrow-right.svg";
import clockIcon from "../../../assets/icons/adaptive-clock-icon.svg";
import { ReactComponent as SearchIcon } from "../../../assets/icons/adaptive-search-icon.svg";
import { Colors } from "../../../styles/constant";
import NotFoundGif from "../../../assets/gif/Not_found.json";
import "./AdaptiveScreenSearch.scss";
import CommonGifAnimation from "../../../components/CustomGifAnimation/CommonGifAnimation";
import {
  PolicyOrAccountSelectionData,
  T_AnimationData,
  T_LiRouteState,
} from "../../../globalTypes/GlobalTypes";
import {
  useLazyFetchPopularServiceQuery,
  useLazyFetchSearchHistoryQuery,
  useLazyFetchSearchBarContentQuery,
  useLazySaveSearchHistoryQuery,
  useClearSearchHistoryMutation,
} from "../GeneralAppElements_Api";
import {
  T_fetchSearchHistory,
  T_P1orP2,
  T_SearchBarContent,
} from "../GeneralAppElements_Types";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import { useNavigate } from "react-router-dom";
import CsSessionStorageUtils from "../../../utils/CsSessionStorageUtils";
import {
  defaultPopularServicesLobList,
  GTAScreenType,
  HiLobCode,
  HlLobCode,
  LiLobCode,
  MfLobCode,
  SCREEN_TYPE,
  StockLobCode,
} from "../../../utils/constants";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { TriggerGenerateSSO_OLD } from "../../Feature_SSO/Sso_Types";
import { isEmpty } from "lodash";
import CustomCircularLoader from "../../../components/CustomCircularLoader/CustomCircularLoader";
import CustomPolicyOrAccountSelect from "../../../components/CustomPolicyOrAccountSelect/CustomPolicyOrAccountSelect";
import HomeLoansIcon from "../../../assets/icons/home-loan-popular-service-icon.svg";
import PersonalLoansIcon from "../../../assets/icons/personal-loan-popular-service-icon.svg";
import {
  getCmsConfigMarquee,
  getCmsConfigPopularServices,
  getCmsExplorePlans,
  getDefaultMarqueeText,
  getLobsToSearch,
  initializeMarquee,
  windowOpen,
} from "../../../utils/helper";
import { useLazyFetchPolicyInsuredQuery } from "../../Feature_HealthInsurance/HealthInsurance_Api";
import { triggerCleverTapEvent } from "../../../utils/CleverTap";
import TriggerGTAEvent from "../../../utils/GTA";
import CustomNetworkImage from "../../../components/CustomNetworkImage/CustomNetworkImage";
import { useLobRedirection } from "../../../hooks/useLobRedirection";
import { setIsDowntimeForServiceMenuItem } from "../GeneralAppElements_Slice";
import CustomServiceDownTimePopUp from "../../../components/CustomServiceDownTimePopUp/CustomServiceDownTimePopUp";

const titles = {
  SEARCH_HISTORY: "Search History",
  SEARCH_RESULT: "Search Result",
  NOT_FOUND: "Not found",
};

const serviceIcons: Record<string, string> = {
  "Mutual Funds": MutualFundIcon,
  "Stocks & Securities": StockIcon,
  "Life Insurance": LifeInsuranceIcon,
  "Health Insurance": HealthInsuranceIcon,
  "Home Loans": HomeLoansIcon,
  "Personal Loans/Credit Card": PersonalLoansIcon,
};

interface Props {
  open: boolean;
  handleOpen: Function;
}

const AdaptiveScreenSearch: FC<Props> = ({ open, handleOpen }) => {
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);
  const { redirectToLob, loadingState } = useLobRedirection();
  const LI_dashboardDetails = useAppSelector(
    (state) => state.LifeInsuranceSlice,
  );
  const MF_folioDetails = useAppSelector(
    (state) => state.MutualFundSlice.folioDetails,
  );
  const HI_policyDetails = useAppSelector(
    (state) => state.HealthInsuranceSlice.allPolicyDetails,
  );
  const STOCKS_Details = useAppSelector(
    (state) => state.StocksSlice.stockDashboardDetails,
  );
  const HL_LoanInfo = useAppSelector((state) => state.HomeLoanSlice.loanInfo);
  const [searchState, setSearchState] = useState(titles.SEARCH_HISTORY);
  const [searchValue, setSearchValue] = useState("");
  const [showAllResults, setShowAllResults] = useState(false);
  const [policyOrAccountSelectionData, setPolicyOrAccountSelectionData] =
    useState<PolicyOrAccountSelectionData>({
      isOpen: false,
      options: [],
      redirectionUrl: "",
      lobDetails: null,
      functionalityCode: "",
    });
  const itemsPerPage = 5;
  const [filteredResults, setFilteredResults] = useState<T_SearchBarContent[]>(
    [],
  );
  const [listItemLoading, setListItemLoading] = useState<{
    key: string;
    loading: boolean;
  }>();
  const navigate = useNavigate();
  const [fetchPopularService, { data: popularServiceData }] =
    useLazyFetchPopularServiceQuery();
  const [fetchSearchHistory, { data: searchHistoryData }] =
    useLazyFetchSearchHistoryQuery();
  const [fetchSearchBarContent, { data: searchBarContent }] =
    useLazyFetchSearchBarContentQuery();
  const [saveSearchHistory] = useLazySaveSearchHistoryQuery();
  const [clearSearchHistory] = useClearSearchHistoryMutation();
  const [fetchPolicyInsuredApi] = useLazyFetchPolicyInsuredQuery();
  const mappedSearchHistoryData = searchHistoryData?.payload || [];
  const masterData = useAppSelector(
    (state) => state.GeneralAppElementsSlice.masterData,
  );
  const activeTab = CsSessionStorageUtils.get("ACTIVE_DASHBOARD_TAB");
  const CsAppSlice = useAppSelector((state) => state.CsAppSlice);
  const ssoSlice = useAppSelector(
    (state) => state.SsoSlice.ssoData as TriggerGenerateSSO_OLD,
  );
  const dispatch = useAppDispatch();
  const popularServicesLob = masterData?.masterDetail?.popularServicesLob;
  const cmsSlice = useAppSelector((state) => state.CmsSlice);

  const masterDetail = masterData?.masterDetail;

  let networkHospitalRedirectionUrl =
    masterDetail?.["network-hospital"]?.[0].masterValue;

  let callADoctorRedirectionUrl =
    masterDetail?.["call-a-doctor"]?.[0].masterValue;

  useEffect(() => {
    if (!ssoSlice || isEmpty(ssoSlice) || !ssoSlice?.showLoading) {
      setListItemLoading((prev) => {
        if (prev) {
          return { ...prev, loading: false };
        } else {
          return undefined;
        }
      });
    }
  }, [ssoSlice]);

  useEffect(() => {
    setListItemLoading(loadingState);
  }, [loadingState]);

  const cmsConfigPopularServices = useMemo(
    () => getCmsConfigPopularServices(cmsSlice.CmsIntegrationVariableData),
    [],
  );

  const cmsExplorePlans = useMemo(
    () => getCmsExplorePlans(cmsSlice.CmsIntegrationVariableData),
    [],
  );

  const cmsConfigMarquee = useMemo(
    () => getCmsConfigMarquee(cmsSlice.CmsIntegrationVariableData, activeTab),
    [],
  );

  const defaultMarqueeText = getDefaultMarqueeText(
    cmsSlice.CmsIntegrationVariableData,
  );

  const [marqueeLabels, setMarqueeLabels] = useState(defaultMarqueeText);

  useEffect(() => {
    const cleanup = initializeMarquee(
      cmsConfigMarquee,
      setMarqueeLabels,
      defaultMarqueeText,
    );
    return cleanup; // Cleanup function to clear interval on unmount
  }, [cmsConfigMarquee, defaultMarqueeText]);

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const inputValue = event.target.value.toLowerCase();

    if (inputValue === "") {
      setSearchState(titles.SEARCH_HISTORY);
    } else if (inputValue.length >= 2) {
      setSearchState(titles.SEARCH_RESULT);

      const filtered =
        searchBarContent?.payload?.filter((item) =>
          item.menuDisplayText.toLowerCase().includes(inputValue),
        ) || [];
      if (filtered.length <= 0) {
        setSearchState(titles.NOT_FOUND);
      } else {
        triggerCleverTapEvent("hd_serv_search_term_select", {
          ScreenType: SCREEN_TYPE.DashboardScreenType,
          isPrevPageNameRequired: true,
          ScreenName: "Servicing Search Screen",
          SectionTitle: "search_results",
          SearchText: inputValue,
        });
        setFilteredResults(filtered);
      }
    } else {
      setSearchState(titles.SEARCH_HISTORY);
    }
    setSearchValue(event.target.value);
  };

  React.useEffect(() => {
    const data = {
      csUserId: userDetails?.CS_USER_ID || "",
    };
    fetchSearchHistory(data);
    const params = {
      lobs:
        popularServicesLob?.map((p) => p.masterValue) ||
        defaultPopularServicesLobList,
    };
    fetchPopularService(params, true);
    const lobsToSearch = getLobsToSearch(
      CsAppSlice.userDetails,
      popularServicesLob,
    );
    fetchSearchBarContent({ lob: lobsToSearch.join(",") });
  }, []);

  React.useEffect(() => {
    if (searchBarContent?.payload) {
      setFilteredResults(searchBarContent.payload);
    }
  }, [searchBarContent]);

  const handleGTACTAClick = (sectionTitle: string, searchText: string) => {
    TriggerGTAEvent("hd_serv_search_term_select", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "servicing_search_screen",
      section_title: sectionTitle,
      search_text: searchText,
    });
  };

  const handleSearchHistoryArrowClick = (history: T_fetchSearchHistory) => {
    setFilteredResults([]);
    handleGTACTAClick(searchState, searchValue);
    if (history.redirectionMode == "inApp") {
      handleInAppRedirection(
        history.lob,
        history.redirectionUrl,
        history.functionalityCode || "",
      );
    } else {
      if (history?.isDowntime === true) {
        dispatch(setIsDowntimeForServiceMenuItem(true));
        return true;
      }
      redirectToLob(
        "" + history.uniqueId,
        "" + history.functionalityCode,
        history.lob,
        history.redirectionUrl,
        true,
      );
    }
  };

  const handleArrowClick = useCallback((value: T_SearchBarContent) => {
    handleGTACTAClick(searchState, searchValue);
    saveSearchHistory({
      csUserId: userDetails?.CS_USER_ID || "",
      searchHistory: [value.uniqueId.toString()],
    }).then((r) => {
      if (value.redirectionMode === "inApp") {
        handleInAppRedirection(
          value.lob,
          value.redirectionUrl,
          value.functionalityCode,
        );
      } else {
        if (value?.isDowntime === true) {
          dispatch(setIsDowntimeForServiceMenuItem(true));
          return true;
        }
        redirectToLob(
          `${value.uniqueId}-${value.menuId}`,
          "" + value.functionalityCode,
          value.lob,
          value.redirectionUrl,
          true,
        );
      }
    });
  }, []);

  const onDialogCloseHandler = useCallback(() => {
    handleOpen(false);
    setSearchState(titles.SEARCH_HISTORY);
    setSearchValue("");
    setShowAllResults(false);
  }, []);

  const clearSearchHistoryHandler = useCallback(() => {
    const data = { csUserId: userDetails?.CS_USER_ID || "" };
    clearSearchHistory(data);
  }, []);

  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const displayedResults = showAllResults
    ? filteredResults
    : filteredResults.slice(0, itemsPerPage);

  const handleViewAll = () => {
    setShowAllResults(true);
  };

  const handlePopularServicesClick = useCallback(
    (lobName: string, option: T_P1orP2) => {
      triggerCleverTapEvent("hd_serv_popular_search_item_click", {
        ScreenType: SCREEN_TYPE.DashboardScreenType,
        isPrevPageNameRequired: true,
        ScreenName: "Servicing Search Screen",
        CardTitle: lobName,
      });

      TriggerGTAEvent("hd_serv_popular_search_item_click", {
        screen_type: GTAScreenType.DashboardScreenType,
        screen_name: "servicing_search_screen",
        card_title: lobName,
      });

      if (option.redirectionMode === "inApp") {
        handleInAppRedirection(lobName, option.redirectionUrl);
      } else {
        if (option?.isDowntime === true) {
          dispatch(setIsDowntimeForServiceMenuItem(true));
          return true;
        }
        redirectToLob(
          `${lobName}`,
          "" + option.functionalityCode,
          lobName,
          option.redirectionUrl,
          true,
        );
      }
    },
    [],
  );

  const handleSearchClick = () => {
    const searchItem = filteredResults.find(
      (item) =>
        item.menuDisplayText.toLowerCase() === searchValue.toLowerCase(),
    );
    if (searchItem) {
      handleArrowClick(searchItem);
    } else {
      setSearchState(titles.NOT_FOUND);
    }
  };

  const handleClearClick = () => {
    setSearchValue("");
  };

  const handleInAppRedirection = (
    lobName: string,
    redirectionUrl: string,
    functionalityCode?: string,
  ) => {
    const lobDetails = masterData?.productCategory.find(
      (f) => f.lob === lobName || f.lobName === lobName || f.lobId === lobName,
    );
    const lobList = [LiLobCode, HiLobCode, MfLobCode, StockLobCode];
    if (lobList.includes(lobDetails?.lob || "")) {
      navigate(redirectionUrl);
    } else if (lobDetails?.lob === HlLobCode) {
      const stateData = {
        functionalityCode: functionalityCode,
      };
      navigate(redirectionUrl, { state: stateData });
    } else {
      onDialogCloseHandler();
    }
  };

  const policyOrAccountDataHandler = (data: PolicyOrAccountSelectionData) => {
    setPolicyOrAccountSelectionData(data);
  };

  const handleRedirect = (url: string) => {
    windowOpen(url);
  };

  const popularServicesUI = useMemo(() => {
    if (!popularServiceData?.payload) {
      return <></>;
    }
    const handleExplorePlans = (
      headingData: string,
      title: string,
      redirectionUrl: string,
    ) => {
      return (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ color: "#414042" }}
              style={{
                cursor: "pointer",
                wordBreak: "break-all",
                textDecoration: "none",
                fontSize: ".75rem",
                fontWeight: "500",
                lineHeight: ".975rem",
                color: "#414042",
              }}
              onClick={() => {
                triggerCleverTapEvent("hd_serv_popular_search_item_click", {
                  ScreenType: SCREEN_TYPE.DashboardScreenType,
                  isPrevPageNameRequired: true,
                  ScreenName: "Servicing Search Screen",
                  CardTitle: headingData,
                });
                if (!listItemLoading?.loading) {
                  handleRedirect(redirectionUrl);
                }
              }}
            >
              {title}
            </Typography>
          </Box>
        </>
      );
    };
    type T_ExplorePlans = {
      lobDisplayName: string;
      lob: string;
      text: string;
      redirectionUrl: string;
    };
    const explorePlansMapped: T_ExplorePlans[] = [];
    const mappedServices = popularServiceData.payload.map((p) => {
      const cmsConfigP1 = cmsConfigPopularServices.find(
        (f) => f.attributes?.variableId === p.p1?.cmsId,
      )?.attributes;
      const cmsConfigP2 = cmsConfigPopularServices.find(
        (f) => f.attributes?.variableId === p.p2?.cmsId,
      )?.attributes;
      const explorePlans = cmsExplorePlans.find(
        (f) => f.attributes?.lobId === cmsConfigP1?.lobId,
      );
      const userLOBDetail =
        CsAppSlice?.userDetails?.customerAndLobDetails?.filter(
          (detail) =>
            [detail.LOB, detail.LOB_CODE].includes(
              explorePlans?.attributes.lobId || "",
            ) ||
            [detail.LOB, detail.LOB_CODE].includes(
              explorePlans?.attributes.lobName || "",
            ),
        ) || [];

      if (userLOBDetail?.length <= 0) {
        explorePlansMapped.push({
          lobDisplayName: cmsConfigP1?.lobName || cmsConfigP2?.lobName || p.lob,
          lob: p.lob,
          text: explorePlans?.attributes?.text || "",
          redirectionUrl: explorePlans?.attributes?.url || "",
        });
      }
      const iconUrlFromP1 = cmsConfigP1?.image.data?.attributes.url ?? "";
      const iconUrlFromP2 = cmsConfigP2?.image.data?.attributes.url ?? "";
      return {
        ...p,
        lobDisplayName: cmsConfigP1?.lobName || cmsConfigP2?.lobName || p.lob,
        iconUrl: iconUrlFromP1 || iconUrlFromP2 || "",
        p1: {
          ...p.p1,
          functionalityCode: "" + p.p1?.functionalityCode,
          functionalityDesc: "",
          menuDisplayValue: "" + cmsConfigP1?.key,
          redirectionMode: "" + cmsConfigP1?.reference,
          redirectionUrl: "" + cmsConfigP1?.url,
          useCaseId: "" + cmsConfigP1?.text,
        },
        p2: {
          ...p.p2,
          functionalityCode: "" + p.p2?.functionalityCode,
          functionalityDesc: "",
          menuDisplayValue: "" + cmsConfigP2?.key,
          redirectionMode: "" + cmsConfigP2?.reference,
          redirectionUrl: "" + cmsConfigP2?.url,
          useCaseId: "" + cmsConfigP2?.text,
        },
      };
    });
    return !mappedServices || mappedServices?.length <= 0 ? (
      <></>
    ) : (
      <Box bgcolor="#F7F7F7" padding="1.25rem 1.5rem">
        <Box padding="1rem" bgcolor="white" borderRadius="1rem">
          <Typography fontWeight={500} lineHeight="1.5rem" fontSize="1rem">
            Popular Services
          </Typography>
          <Grid container marginTop="0.375rem" spacing="0.625rem">
            {mappedServices.map((item) => {
              const explorePlanItem = explorePlansMapped.find(
                (f) => f.lob === item.lob,
              );
              return (
                <Grid item xs={6} key={item.lob}>
                  <ListItem alignItems="center">
                    <ListItemAvatar
                      style={{
                        minHeight: "8%",
                        minWidth: "8%",
                        flex: "auto",
                      }}
                    >
                      <Avatar style={{ backgroundColor: "white" }}>
                        <CustomNetworkImage
                          key={item.lobDisplayName}
                          alt={item.lobDisplayName}
                          src={item.iconUrl}
                          fallBackImage={
                            <Avatar alt="img" src={serviceIcons[item.lob]} />
                          }
                        ></CustomNetworkImage>
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      style={{ flex: "80%" }}
                      sx={{ marginLeft: "0.5rem" }}
                      primary={
                        <Typography
                          fontSize=".75rem"
                          fontWeight={400}
                          lineHeight=".975rem"
                        >
                          {item.lobDisplayName}
                        </Typography>
                      }
                      secondary={
                        <div style={{ display: "flex", flexWrap: "wrap" }}>
                          <React.Fragment
                            key={`${item.lob}-${item.p1.useCaseId}`}
                          >
                            {explorePlanItem ? (
                              handleExplorePlans(
                                item.lob,
                                explorePlanItem.text || "Explore Plans",
                                "" + explorePlanItem.redirectionUrl,
                              )
                            ) : (
                              <>
                                <Typography
                                  width={"auto"}
                                  marginTop=".25rem"
                                  fontSize=".75rem"
                                  fontWeight={500}
                                  lineHeight=".975rem"
                                  color="#414042"
                                  style={{
                                    cursor: "pointer",
                                    wordBreak: "break-all",
                                  }}
                                  onClick={() => {
                                    if (!listItemLoading?.loading) {
                                      handlePopularServicesClick(
                                        item.lob,
                                        item.p1,
                                      );
                                    }
                                  }}
                                >
                                  {item.p1.useCaseId}
                                </Typography>
                                {item.p2.useCaseId !== "undefined" && (
                                  <Typography
                                    marginTop=".25rem"
                                    fontSize=".75rem"
                                    fontWeight={500}
                                    lineHeight=".975rem"
                                    color="#414042"
                                    style={{
                                      cursor: "pointer",
                                      wordBreak: "break-all",
                                    }}
                                    onClick={() => {
                                      if (!listItemLoading?.loading) {
                                        handlePopularServicesClick(
                                          item.lob,
                                          item.p2,
                                        );
                                      }
                                    }}
                                  >
                                    <span>&nbsp;{`|`}&nbsp;</span>
                                    {item.p2.useCaseId}
                                  </Typography>
                                )}
                              </>
                            )}
                          </React.Fragment>
                        </div>
                      }
                    />
                    {listItemLoading &&
                      listItemLoading.key === `${item.lob}` &&
                      listItemLoading.loading && (
                        <div style={{ flex: "auto", alignSelf: "center" }}>
                          <CustomCircularLoader size={20} />
                        </div>
                      )}
                  </ListItem>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
    );
  }, [popularServiceData?.payload, listItemLoading]);

  return (
    <Fragment>
      <Dialog
        fullWidth
        className="search-dialog"
        onClose={onDialogCloseHandler}
        open={open}
      >
        <TextField
          fullWidth
          placeholder={marqueeLabels}
          value={searchValue}
          onChange={handleChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearchClick}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: searchValue.length > 0 && (
              <InputAdornment position="end">
                <IconButton sx={{ padding: 0 }} onClick={handleClearClick}>
                  <CloseIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              "& input::placeholder": {
                color: "#737373", // Change this to the desired color
              },
            },
          }}
        />

        <Box className="search-dialog-body">
          {searchState === "Search History" && (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h4">{searchState}</Typography>
                {mappedSearchHistoryData.length > 0 && (
                  <Button variant="text">
                    <Typography
                      variant="h5"
                      color={Colors.red}
                      onClick={clearSearchHistoryHandler}
                    >
                      Clear all
                    </Typography>
                  </Button>
                )}
              </Box>
              <Stack marginTop="1rem" marginBottom="3rem">
                {mappedSearchHistoryData.length > 0 &&
                  mappedSearchHistoryData.map((item, index) => (
                    <React.Fragment key={item.uniqueId}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        marginY="1rem"
                        sx={{
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          if (!listItemLoading?.loading) {
                            handleSearchHistoryArrowClick(item);
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" gap="0.5rem">
                          <img src={clockIcon} />
                          <Typography variant="h5" fontWeight={500}>
                            {item.menuDisplayText}
                          </Typography>
                        </Box>
                        {listItemLoading &&
                        listItemLoading.key === "" + item.uniqueId &&
                        listItemLoading.loading ? (
                          <CustomCircularLoader size={20} />
                        ) : (
                          <img
                            style={{ cursor: "pointer" }}
                            src={arrowRight}
                            alt="arrow-right"
                          />
                        )}
                      </Box>
                      {mappedSearchHistoryData.length - 1 > index && (
                        <Divider sx={{ width: "84%" }} />
                      )}
                    </React.Fragment>
                  ))}
              </Stack>
            </>
          )}
          {searchState === "Search Result" && (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h4">{searchState}</Typography>
              </Box>
              <Stack marginTop="1rem">
                {(showAllResults
                  ? filteredResults
                  : filteredResults.slice(0, itemsPerPage)
                ).map((item, index) => (
                  <React.Fragment key={`${item.uniqueId}-${item.menuId}`}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      marginY="1rem"
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (!listItemLoading?.loading) {
                          handleArrowClick(item);
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap="0.5rem">
                        <SearchIcon />
                        <Typography variant="h5" fontWeight={500}>
                          {item.menuDisplayText}
                        </Typography>
                      </Box>
                      {listItemLoading &&
                      listItemLoading.key ===
                        `${item.uniqueId}-${item.menuId}` &&
                      listItemLoading.loading ? (
                        <CustomCircularLoader size={20} />
                      ) : (
                        <img
                          style={{ cursor: "pointer" }}
                          src={arrowRight}
                          alt="arrow-right"
                        />
                      )}
                    </Box>
                    {displayedResults.length - 1 > index && (
                      <Divider sx={{ width: "84%" }} />
                    )}
                  </React.Fragment>
                ))}
                {filteredResults.length > itemsPerPage && !showAllResults && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="text"
                      sx={{
                        minWidth: "1.25rem",
                        height: "1.25rem",
                        color: Colors.red,
                        marginTop: "1.25rem",
                      }}
                      onClick={handleViewAll}
                    >
                      View All
                    </Button>
                  </Box>
                )}
              </Stack>
            </>
          )}
          {searchState === "Not found" && (
            <Box>
              <Box className="search-dialog-body">
                <CommonGifAnimation
                  animationData={NotFoundGif as T_AnimationData}
                  width="200px"
                />
                <Typography
                  sx={{
                    fontWeight: "600",
                    fontSize: "1.25rem",
                    lineHeight: "1.5rem",
                    color: "#2E2E2ECC",
                    textAlign: "center",
                  }}
                >
                  {"Uhh-Ohh!"}
                </Typography>
                <Typography
                  sx={{
                    marginTop: "0.5rem",
                    fontWeight: "400",
                    fontSize: ".75rem",
                    lineHeight: "1.05rem",
                    color: "rgba(46, 46, 46, 0.5)",
                    textAlign: "center",
                  }}
                >
                  {"We couldn't find what you were looking for"}
                </Typography>
              </Box>
            </Box>
          )}
          {["Not found", "Search History"].includes(searchState) &&
            popularServicesUI}
        </Box>
      </Dialog>
      {policyOrAccountSelectionData.isOpen &&
        !isEmpty(policyOrAccountSelectionData.options) && (
          <CustomPolicyOrAccountSelect
            data={policyOrAccountSelectionData}
            policyOrAccountDataHandler={policyOrAccountDataHandler}
          />
        )}
      <CustomServiceDownTimePopUp />
    </Fragment>
  );
};

export default AdaptiveScreenSearch;
